<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import PageHeader from '../../SettingsSubPageHeader.vue';
import AgentSelector from '../AgentSelector.vue';
import Spinner from 'shared/components/Spinner.vue';

export default {
  name: 'EditTeamModal',
  components: { PageHeader, AgentSelector, Spinner },
  props: {
    teamId: { type: [String, Number], required: true },
  },
  emits: ['close', 'updated'],
  validations() {
    return {
      form: {
        name: {
          required: helpers.withMessage(
            this.$t('TEAMS_SETTINGS.FORM.NAME.REQUIRED'),
            required
          ),
        },
        description: {},
      },
      selectedAgents: {
        isEmpty: helpers.withMessage(
          this.$t('TEAMS_SETTINGS.ADD.AGENT_VALIDATION_ERROR'),
          value => Array.isArray(value) && value.length > 0
        ),
      },
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      form: { name: '', description: '' },
      selectedAgents: [],
      isSaving: false,
      loaded: false,
    };
  },
  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
      teamUI: 'teams/getUIFlags',
      memberUI: 'teamMembers/getUIFlags',
    }),
    currentTeam() {
      return this.$store.getters['teams/getTeam'](this.teamId) || {};
    },
    headerTitle() {
      return this.$t('TEAMS_SETTINGS.EDIT_FLOW.CREATE.TITLE');
    },
    isFetching() {
      return (this.teamUI?.isFetching || this.memberUI?.isFetching) && !this.loaded;
    },
  },
  async mounted() {
    // Load prerequisites
    this.$store.dispatch('agents/get');
    await this.$store.dispatch('teams/get');

    // Prefill form
    this.form.name = this.currentTeam.name || '';
    this.form.description = this.currentTeam.description || '';

    // Load current members
    try {
      await this.$store.dispatch('teamMembers/get', { teamId: this.teamId });
      const members = this.$store
        .getters['teamMembers/getTeamMembers'](this.teamId)
        .map(m => m.id);
      this.selectedAgents = members;
    } catch {
      this.selectedAgents = [];
    } finally {
      this.loaded = true;
    }
  },
  methods: {
    onCancel() {
      this.$emit('close');
    },
    updateSelectedAgents(list) {
      this.v$.selectedAgents.$touch();
      this.selectedAgents = [...list];
    },
    async onSave() {
      this.v$.$touch();
      if (this.v$.$invalid) return;

      this.isSaving = true;
      try {
        // 1) Update team info
        await this.$store.dispatch('teams/update', {
          id: this.teamId,
          name: this.form.name,
          description: this.form.description,
        });

        // 2) Update members
        await this.$store.dispatch('teamMembers/update', {
          teamId: this.teamId,
          agentsList: this.selectedAgents,
        });

        this.$store.dispatch('teams/get');
        useAlert(this.$t('GENERAL.UPDATED_SUCCESSFULLY'), {type: 'success'});
        this.$emit('updated');
        this.$emit('close');
      } catch (e) {
        useAlert(e?.message || 'Error', {type: 'error'});
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<template>
  <div class="modal-content w-full">
    <PageHeader
      :header-title="headerTitle"
    />

    <div v-if="isFetching" class="py-8">
      <Spinner />
    </div>

    <div v-else class="grid gap-6">
      <!-- Team name -->
      <woot-input
        v-model="form.name"
        :label="$t('TEAMS_SETTINGS.FORM.NAME.LABEL')"
        :placeholder="$t('TEAMS_SETTINGS.FORM.NAME.PLACEHOLDER')"
        :error="v$.form.name.$error ? v$.form.name.$errors[0].$message : ''"
        @blur="v$.form.name.$touch"
      />

      <!-- Description -->
      <woot-input
        v-model="form.description"
        :label="$t('TEAMS_SETTINGS.FORM.DESCRIPTION.LABEL')"
        :placeholder="$t('TEAMS_SETTINGS.FORM.DESCRIPTION.PLACEHOLDER')"
      />

      <!-- Members -->
      <div class="setting-table-container">
        <div v-if="v$.selectedAgents.$error" class="mb-2 text-red-500 text-sm">
          {{ v$.selectedAgents.$errors[0].$message }}
        </div>
        <AgentSelector
          :agent-list="agentList"
          :selected-agents="selectedAgents"
          :update-selected-agents="updateSelectedAgents"
          :is-working="isSaving"
          :show-submit-button="false"
          :submit-button-text="$t('TEAMS_SETTINGS.EDIT_FLOW.AGENTS.BUTTON_TEXT')"
        />
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-center gap-3 mt-2">
        <woot-button
          variant="clear"
          color-scheme="secondary"
          class-names="!rounded-lg"
          @click="onCancel"
        >
          {{ $t('GENERAL.CANCEL') }}
        </woot-button>
        <woot-button
          :is-loading="isSaving"
          class="!bg-grass-500 !text-white rounded-lg"
          @click="onSave"
        >
          {{ $t('GENERAL.SAVE') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>